/**
 * Production Monitoring Service
 * Tracks real user metrics, errors, and performance
 */

import * as Sentry from '@sentry/react';

interface PerformanceMetric {
  name: string;
  value: number;
  unit: 'ms' | 'bytes' | 'count' | 'score';
  tags?: Record<string, string>;
}

interface UserEvent {
  eventName: string;
  properties?: Record<string, any>;
  timestamp: number;
  sessionId: string;
  userId?: string;
}

interface ErrorEvent {
  message: string;
  stack?: string;
  componentStack?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

export class MonitoringService {
  private sessionId: string;
  private userId?: string;
  private events: UserEvent[] = [];
  private metrics: PerformanceMetric[] = [];
  private flushInterval: number = 30000; // 30 seconds
  private flushTimer?: NodeJS.Timeout;
  private observer?: PerformanceObserver;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializePerformanceObserver();
    this.initializeErrorHandling();
    this.startFlushTimer();
    this.initializeSentry();
  }

  /**
   * Initialize performance observer for Core Web Vitals
   */
  private initializePerformanceObserver() {
    if (typeof PerformanceObserver === 'undefined') return;

    try {
      // Observe Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        this.trackMetric({
          name: 'web_vital_lcp',
          value: lastEntry.renderTime || lastEntry.loadTime,
          unit: 'ms',
          tags: { page: window.location.pathname }
        });
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // Observe First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          this.trackMetric({
            name: 'web_vital_fid',
            value: entry.processingStart - entry.startTime,
            unit: 'ms',
            tags: { eventType: entry.name }
          });
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Observe Cumulative Layout Shift
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        this.trackMetric({
          name: 'web_vital_cls',
          value: clsValue,
          unit: 'score',
          tags: { page: window.location.pathname }
        });
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });

      // Track page load metrics
      window.addEventListener('load', () => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        this.trackMetric({
          name: 'page_load_time',
          value: navigation.loadEventEnd - navigation.fetchStart,
          unit: 'ms'
        });

        this.trackMetric({
          name: 'dom_ready_time',
          value: navigation.domContentLoadedEventEnd - navigation.fetchStart,
          unit: 'ms'
        });

        // Track resource metrics
        const resources = performance.getEntriesByType('resource');
        const jsSize = resources
          .filter(r => r.name.endsWith('.js'))
          .reduce((sum, r: any) => sum + (r.transferSize || 0), 0);
        
        this.trackMetric({
          name: 'js_bundle_size',
          value: jsSize,
          unit: 'bytes'
        });
      });
    } catch (error) {
      console.warn('Failed to initialize performance observer:', error);
    }
  }

  /**
   * Initialize global error handling
   */
  private initializeErrorHandling() {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.trackError({
        message: event.message,
        stack: event.error?.stack,
        severity: 'high',
        context: {
          filename: event.filename,
          line: event.lineno,
          column: event.colno,
          type: 'unhandled_error'
        }
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        severity: 'high',
        context: {
          type: 'unhandled_promise_rejection'
        }
      });
    });
  }

  /**
   * Set the current user ID
   */
  setUserId(userId: string) {
    this.userId = userId;
    this.trackEvent('user_identified', { userId });
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, properties?: Record<string, any>) {
    const event: UserEvent = {
      eventName,
      properties,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      userId: this.userId
    };

    this.events.push(event);

    // Track critical business events immediately
    if (this.isCriticalEvent(eventName)) {
      this.flush();
    }
  }

  /**
   * Track a performance metric
   */
  trackMetric(metric: PerformanceMetric) {
    this.metrics.push({
      ...metric,
      tags: {
        ...metric.tags,
        sessionId: this.sessionId,
        userId: this.userId || 'anonymous'
      }
    });
  }

  /**
   * Track an error
   */
  trackError(error: ErrorEvent) {
    // Send errors immediately
    this.sendToMonitoring('/api/monitoring/errors', {
      ...error,
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Also track as event
    this.trackEvent('error', {
      message: error.message,
      severity: error.severity,
      ...error.context
    });
  }

  /**
   * Track game-specific metrics
   */
  trackGameMetrics(metrics: {
    screen?: string;
    action?: string;
    karmaChange?: number;
    combatResult?: 'victory' | 'defeat' | 'draw';
    storyChoice?: number;
    loadTime?: number;
  }) {
    // Track as structured event
    this.trackEvent('game_action', metrics);

    // Track specific metrics
    if (metrics.loadTime) {
      this.trackMetric({
        name: 'game_screen_load_time',
        value: metrics.loadTime,
        unit: 'ms',
        tags: { screen: metrics.screen || 'unknown' }
      });
    }

    if (metrics.karmaChange !== undefined) {
      this.trackMetric({
        name: 'karma_change',
        value: metrics.karmaChange,
        unit: 'count',
        tags: { action: metrics.action || 'unknown' }
      });
    }
  }

  /**
   * Start automatic flushing of events
   */
  private startFlushTimer() {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });

    // Flush on visibility change (mobile background)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flush();
      }
    });
  }

  /**
   * Send collected data to monitoring endpoints
   */
  private async flush() {
    // Send events if any
    if (this.events.length > 0) {
      const eventsToSend = [...this.events];
      this.events = [];
      
      await this.sendToMonitoring('/api/monitoring/events', {
        sessionId: this.sessionId,
        events: eventsToSend
      });
    }

    // Send metrics if any
    if (this.metrics.length > 0) {
      const metricsToSend = [...this.metrics];
      this.metrics = [];
      
      await this.sendToMonitoring('/api/monitoring/metrics', {
        sessionId: this.sessionId,
        metrics: metricsToSend
      });
    }
  }

  /**
   * Send data to monitoring endpoint (Sentry only - no API endpoints)
   */
  private async sendToMonitoring(endpoint: string, data: any) {
    try {
      // Only use Sentry for monitoring - no API endpoints needed
      // This prevents 405 errors from non-existent /api/monitoring/* endpoints
      console.log(`[Monitoring] Data collected for ${endpoint}:`, data);
      
      // Send to Sentry instead of API endpoint
      if (endpoint.includes('events')) {
        Sentry.addBreadcrumb({
          category: 'monitoring',
          message: 'User events collected',
          data: { eventCount: data.events?.length }
        });
      } else if (endpoint.includes('metrics')) {
        Sentry.addBreadcrumb({
          category: 'performance',
          message: 'Metrics collected',
          data: { metricCount: data.metrics?.length }
        });
      } else if (endpoint.includes('errors')) {
        // Error already sent to Sentry above
        console.log('[Monitoring] Error logged to Sentry');
      }
    } catch (error) {
      console.warn('Failed to process monitoring data:', error);
    }
  }

  /**
   * Check if event is critical and should be sent immediately
   */
  private isCriticalEvent(eventName: string): boolean {
    const criticalEvents = [
      'payment_initiated',
      'payment_completed',
      'payment_failed',
      'user_signup',
      'subscription_created',
      'subscription_cancelled',
      'error'
    ];
    
    return criticalEvents.includes(eventName);
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current session analytics
   */
  getSessionAnalytics() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      duration: Date.now() - parseInt(this.sessionId.split('-')[0]),
      eventCount: this.events.length,
      errorCount: this.events.filter(e => e.eventName === 'error').length
    };
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
    this.observer?.disconnect();
  }

  private initializeSentry() {
    // Only initialize if DSN is provided
    if (!import.meta.env.VITE_SENTRY_DSN) {
      console.warn('[MonitoringService] Sentry DSN not configured');
      return;
    }

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      integrations: [
        new Sentry.BrowserTracing({
          // Set sampling to capture 10% of transactions
          tracingOrigins: ['localhost', 'lucid-cyber-rpg.vercel.app', /^\//],
        }),
        new Sentry.Replay({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeSend: (event) => {
        // Filter out non-critical errors
        if (this.shouldAutoHandle(event)) {
          this.triggerDXTAnalysis(event);
        }
        return event;
      }
    });

    // Set user context when available
    if (this.userId) {
      Sentry.setUser({ id: this.userId });
    }
  }

  private shouldAutoHandle(event: Sentry.Event): boolean {
    const autoHandleableErrors = [
      'ChunkLoadError',
      'Script error',
      'Non-Error promise rejection',
      'ResizeObserver loop limit exceeded'
    ];
    
    const errorMessage = event.exception?.values?.[0]?.value || '';
    const isAutoHandleable = autoHandleableErrors.some(pattern => 
      errorMessage.includes(pattern)
    );
    
    const severity = event.level;
    const isNonCritical = severity === 'warning' || severity === 'info';
    
    return isAutoHandleable && isNonCritical;
  }

  private async triggerDXTAnalysis(event: Sentry.Event) {
    try {
      // Create task for DXT extension via MCP
      const task = {
        id: `error_${Date.now()}`,
        timestamp: new Date().toISOString(),
        error: {
          type: event.exception?.values?.[0]?.type || 'UnknownError',
          message: event.exception?.values?.[0]?.value || '',
          stack: event.exception?.values?.[0]?.stacktrace,
          url: event.request?.url || window.location.href,
          userId: event.user?.id,
          browserInfo: {
            userAgent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: event.timestamp
          }
        },
        analysis: {
          severity: event.level,
          category: this.categorizeError(event),
          autoFixable: this.isAutoFixable(event),
          estimatedComplexity: this.estimateComplexity(event)
        },
        status: 'pending',
        metadata: {
          attempts: 0,
          lastAttempt: null,
          humanApprovalRequired: false
        }
      };

      // Save to local storage for DXT pickup
      await this.saveTaskForDXT(task);
      
      // Send notification
      await this.notifyDXT(task);
      
    } catch (error) {
      console.error('Failed to trigger DXT analysis:', error);
    }
  }

  private categorizeError(event: Sentry.Event): string {
    const errorType = event.exception?.values?.[0]?.type || '';
    const errorMessage = event.exception?.values?.[0]?.value || '';
    
    if (errorType.includes('Chunk') || errorMessage.includes('Loading')) {
      return 'resource_loading';
    }
    if (errorType.includes('Network') || errorMessage.includes('fetch')) {
      return 'network';
    }
    if (errorType.includes('TypeError') || errorType.includes('ReferenceError')) {
      return 'javascript';
    }
    if (errorMessage.includes('React') || errorMessage.includes('component')) {
      return 'react_component';
    }
    return 'general';
  }

  private isAutoFixable(event: Sentry.Event): boolean {
    const category = this.categorizeError(event);
    const autoFixableCategories = ['resource_loading', 'react_component'];
    return autoFixableCategories.includes(category);
  }

  private estimateComplexity(event: Sentry.Event): 'low' | 'medium' | 'high' {
    const stackDepth = event.exception?.values?.[0]?.stacktrace?.frames?.length || 0;
    
    if (stackDepth <= 5) return 'low';
    if (stackDepth <= 15) return 'medium';
    return 'high';
  }

  private async saveTaskForDXT(task: any) {
    // Save to indexed DB for DXT extension pickup
    const request = indexedDB.open('dxt_tasks', 1);
    
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('tasks')) {
        db.createObjectStore('tasks', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['tasks'], 'readwrite');
      const store = transaction.objectStore('tasks');
      store.add(task);
    };
  }

  private async notifyDXT(task: any) {
    // Trigger DXT via MCP if available
    if (window.mcpClient) {
      try {
        await window.mcpClient.call('dxt_analyze_error', {
          taskId: task.id,
          error: task.error,
          analysis: task.analysis
        });
      } catch (error) {
        console.warn('MCP notification failed:', error);
      }
    }
  }
}

// Create singleton instance
export const monitoringService = new MonitoringService();

// Auto-initialize in production
if (import.meta.env.PROD) {
  // Track initial page view
  monitoringService.trackEvent('page_view', {
    path: window.location.pathname,
    referrer: document.referrer
  });
}

// Export types for use in other modules
export type { PerformanceMetric, UserEvent, ErrorEvent };